import { Flex, IconButton, Spinner } from '@chakra-ui/react';
import { FaSitemap } from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import { Level, StructureNode } from '@app/api/models/structure';
import { Dialog, Txt } from '@app/components/common';
import { QueryTable } from '@app/components/common/table';
import { useApi, useFetchData } from '@app/hooks';
import { usePermissions } from '@app/hooks/use-permissions';
import { ROLES } from '@app/navigation/types';
import Error404 from '@app/pages/errors/404';
import { useQueryTable } from '@app/components/common/table/query-table';

import { useColumns } from './columns';
import { EditDialog } from './components/edit-dialog';
import { ResubordinateDialog } from './components/resubordinate-dialog';
import { ChainItem } from './types';

export const OrgStructureViewTable = () => {
  const api = useApi();
  const [fetchData] = useFetchData({ method: api.structure.getNodes });
  const [selectedItem, setSelectedItem] = useState<StructureNode>();
  const [modal, setModal] = useState<'edit' | 'add' | 'remove' | 'resubordinate'>();
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(true);
  const hasPermission = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });
  const canChange = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });

  useEffect(() => { 
    (async () => {
      setLoading(true);
      const { data: levelsData } = await api.structure.getLevels({
        filter: {
          is_active: 'true',
        },
        sort: '-value',
        limit: 10,
        offset: 0,
      });
      
      const levels = levelsData.data;
      setLevels(levels);

      setLoading(false);
    })();
  }, []);

  const onEdit = useCallback((item: StructureNode) => {
    setSelectedItem(item);
    setModal('edit');
  }, []);

  const onAdd = useCallback(() => {
    setSelectedItem(null);
    setModal('add');
  }, []);

  const onResubordinate = useCallback((item: StructureNode) => {
    setSelectedItem(item);
    setModal('resubordinate');
  }, []);

  const columns = useColumns({ levels, onEdit, canChange, onResubordinate });

  if (!hasPermission) {return <Error404/>;}

  if (loading) {
    return <><Spinner /></>;
  }

  return <>
    <QueryTable<StructureNode>
      title={<Flex gap={2} alignItems='center'>
        <Txt>Структура организации</Txt>
        <Link to="/org-structure/tree/" aria-label="table-view" >
          <IconButton aria-label="table-view" icon={<FaSitemap color='gray.500' />} />
        </Link>
      </Flex>}
      columns={columns}
      fetchData={fetchData}
      onCreateClick={canChange && onAdd}
      canDownload
    > 
      <EditDialog 
        open={modal === 'edit'}
        levels={levels}
        onClose={() => setModal(undefined)}
        initial={selectedItem}
        onSubmit={() => setModal(undefined)}
        onCancel={() => setModal(undefined)}
      />
      <ResubordinateDialogComponent 
        selectedItem={selectedItem}
        levels={levels}
        open={modal === 'resubordinate'} 
        onClose={() => setModal(undefined)} 
      />
    </QueryTable>
  </>;
};  



const ResubordinateDialogComponent = ({ levels, selectedItem,  open, onClose }: { levels: Level[], selectedItem?: StructureNode, open?: boolean, onClose?: () => void }) => {

  const { refetch } = useQueryTable();

  const [chain, setChain] = useState<ChainItem[]>([]);
  const onResubordinateSubmit  = async () => {
    refetch();
    setChain([]);
    onClose?.();
  };

  return <Dialog open={open}
    onClose={onClose}
    title="Переподчинение"
    width={800}
  >
    <ResubordinateDialog 
      levels={levels}
      parent={open ? selectedItem : chain.find( ({ item }) => item.id === selectedItem?.boss_id)?.item}
      node={selectedItem}
      onSubmit={onResubordinateSubmit}
      onClose={onClose}
    />
  </Dialog>;
};
